import { Box, Card, Chip, Dialog, Stack } from '@mui/material';
import { ReactElement, useState } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  Filter,
  FunctionField,
  List,
  TextField,
  useTranslate,
  SelectInput,
  NumberField,
  TextInput,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../layouts/dashboard';
import { Resources } from '../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import CreateBanner from './Create';
import EditBanner from './Edit';
import './init';
import FilterGroup from '../../components/FilterGroup';

const ListFilter = (props: any) => (
  <Filter {...(props || {})}>
    <FilterGroup alwaysOn spacing={2} p={2}>
      <SelectInput
        source="platform"
        label="module.smallBanners.platform"
        choices={[
          { id: 'ALL', name: 'ALL' },
          { id: 'PC', name: 'PC' },
          { id: 'APP', name: 'APP' },
          { id: 'H5', name: 'H5' },
        ]}
        alwaysOn
      />
      <TextInput source="category" label="module.smallBanners.category" />
    </FilterGroup>
  </Filter>
);

const View = () => {
  const [popup, setPopup] = useState<ReactElement>();
  const translate = useTranslate();
  const formatWithToISOString = (date: Date) => {
    // 步骤1：修正时区（东八区）
    const offset = date.getTimezoneOffset() * 60000; // 东八区为-480分钟 → -28800000毫秒
    const localDate = new Date(date.getTime() - offset);

    // 步骤2：调用toISOString() + 去掉Z后缀 + 裁剪毫秒
    const isoStr = localDate.toISOString(); // 此时是东八区的ISO串：2025-12-18T16:14:00.000Z
    const formatted = isoStr.replace('.000Z', ''); // 去掉毫秒和Z → 2025-12-18T16:14:00

    return formatted;
  };
  return (
    <PermissionGuard resource="small-banners" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('resource.smallBanners')}
          links={[
            { name: translate('resource.smallBanners'), href: `/${Resources.smallBanners}` },
            { name: translate('resource.list') },
          ]}
          action={
            <PermissionGuard resource="small-banners" action="create" fallback={<></>}>
              <CreateButton
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(<CreateBanner onClose={() => setPopup(undefined)} />);
                }}
              />
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            actions={false}
            empty={false}
            filters={<ListFilter />}
            perPage={10}
            exporter={false}
            resource={Resources.smallBanners}
          >
            <Box sx={{ overflowX: 'auto', width: 1 }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-table': {
                    tableLayout: 'fixed',
                    /* 14 列；合计约 1814px */
                    minWidth: 1860,
                    width: '100%',
                  },
                  '& .RaDatagrid-thead th, & .RaDatagrid-tbody td': {
                    whiteSpace: 'nowrap',
                    px: 1.5,
                    py: 1,
                    verticalAlign: 'middle',
                  },
                  /* sortIndex */
                  '& .RaDatagrid-thead th:nth-of-type(1), & .RaDatagrid-tbody td:nth-of-type(1)': {
                    width: 72,
                    minWidth: 72,
                  },
                  /* category */
                  '& .RaDatagrid-thead th:nth-of-type(2), & .RaDatagrid-tbody td:nth-of-type(2)': {
                    width: 100,
                    minWidth: 100,
                  },
                  /* platform */
                  '& .RaDatagrid-thead th:nth-of-type(3), & .RaDatagrid-tbody td:nth-of-type(3)': {
                    width: 72,
                    minWidth: 72,
                  },
                  /* route */
                  '& .RaDatagrid-thead th:nth-of-type(4), & .RaDatagrid-tbody td:nth-of-type(4)': {
                    width: 280,
                    minWidth: 280,
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                  },
                  /* en image */
                  '& .RaDatagrid-thead th:nth-of-type(5), & .RaDatagrid-tbody td:nth-of-type(5)': {
                    width: 110,
                    minWidth: 110,
                  },
                  /* zh image */
                  '& .RaDatagrid-thead th:nth-of-type(6), & .RaDatagrid-tbody td:nth-of-type(6)': {
                    width: 110,
                    minWidth: 110,
                  },
                  /* showButton */
                  '& .RaDatagrid-thead th:nth-of-type(7), & .RaDatagrid-tbody td:nth-of-type(7)': {
                    width: 110,
                    minWidth: 110,
                  },
                  /* enButtonText */
                  '& .RaDatagrid-thead th:nth-of-type(8), & .RaDatagrid-tbody td:nth-of-type(8)': {
                    width: 110,
                    minWidth: 110,
                  },
                  /* zhButtonText */
                  '& .RaDatagrid-thead th:nth-of-type(9), & .RaDatagrid-tbody td:nth-of-type(9)': {
                    width: 110,
                    minWidth: 110,
                  },
                  /* startAt */
                  '& .RaDatagrid-thead th:nth-of-type(10), & .RaDatagrid-tbody td:nth-of-type(10)': {
                    width: 160,
                    minWidth: 160,
                  },
                  /* endAt */
                  '& .RaDatagrid-thead th:nth-of-type(11), & .RaDatagrid-tbody td:nth-of-type(11)': {
                    width: 160,
                    minWidth: 160,
                  },
                  /* clickCount */
                  '& .RaDatagrid-thead th:nth-of-type(12), & .RaDatagrid-tbody td:nth-of-type(12)': {
                    width: 120,
                    minWidth: 120,
                  },
                  /* isActive */
                  '& .RaDatagrid-thead th:nth-of-type(13), & .RaDatagrid-tbody td:nth-of-type(13)': {
                    width: 100,
                    minWidth: 100,
                  },
                  /* actions */
                  '& .RaDatagrid-thead th:nth-of-type(14), & .RaDatagrid-tbody td:nth-of-type(14)': {
                    width: 120,
                    minWidth: 120,
                  },
                }}
              >
                {/*<TextField source="id" label="module.smallBanners.id" sortable={false} />*/}
                <NumberField source="sortIndex" label="module.smallBanners.sortIndex" sortable={false} />

                <TextField source="category" label="module.smallBanners.category" sortable={false} />
                <TextField source="platform" label="module.smallBanners.platform" sortable={false} />
                <FunctionField
                  label="module.smallBanners.route"
                  sortable={false}
                  render={(record: any) => (
                    <Box
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                      }}
                    >
                      {record?.route}
                    </Box>
                  )}
                />
                <FunctionField
                  label="module.smallBanners.isInternalRoute"
                  render={(record: any) => (
                    <Chip
                      label={record.isInternalRoute === true || record.isInternalRoute === 1 ? 'Y' : 'N'}
                      color={record.isInternalRoute === true || record.isInternalRoute === 1 ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                <FunctionField
                  label="module.smallBanners.enImageUrl"
                  render={(record: any) => (
                    <Box
                      component="img"
                      src={record.enImageUrl}
                      alt="Banner"
                      sx={{ maxWidth: 100, maxHeight: 50, objectFit: 'contain' }}
                    />
                  )}
                  sortable={false}
                />
                <FunctionField
                  label="module.smallBanners.zhImageUrl"
                  render={(record: any) => (
                    <Box
                      component="img"
                      src={record.zhImageUrl}
                      alt="Banner"
                      sx={{ maxWidth: 100, maxHeight: 50, objectFit: 'contain' }}
                    />
                  )}
                  sortable={false}
                />

                <FunctionField
                  label="module.smallBanners.showButton"
                  render={(record: any) => (
                    <Chip
                      label={record.showButton === true || record.showButton === 1 ? 'Y' : 'N'}
                      color={record.showButton === true || record.showButton === 1 ? 'success' : 'default'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                <TextField source="enButtonText" label="module.smallBanners.enButtonText" sortable={false} />
                <TextField source="zhButtonText" label="module.smallBanners.zhButtonText" sortable={false} />
                <FunctionField
                  label="module.smallBanners.startAt"
                  render={(record: any) => {
                    const ts = Number(record?.startAt);
                    if (Number.isNaN(ts)) return '';
                    return formatWithToISOString(new Date(ts));
                  }}
                  sortable={false}
                />
                <FunctionField
                  label="module.smallBanners.endAt"
                  render={(record: any) => {
                    const ts = Number(record?.endAt);
                    if (Number.isNaN(ts)) return '';
                    return formatWithToISOString(new Date(ts));
                  }}
                  sortable={false}
                />
                <NumberField
                  source="clickCount"
                  label="module.smallBanners.clickCount"
                  sortable={false}
                />
                <FunctionField
                  label="module.smallBanners.isActive"
                  render={(record: any) => (
                    <Chip
                      label={record.isActive === true ? 'True' : 'False'}
                      color={record.isActive === true ? 'success' : 'error'}
                      size="small"
                    />
                  )}
                  sortable={false}
                />
                <FunctionField
                  render={(record: any) => (
                    <Stack direction="row" spacing={2}>
                      <PermissionGuard resource="small-banners" action="update" fallback={<></>}>
                        <EditButton
                          onClick={(e) => {
                            e.preventDefault();
                            setPopup(
                              <EditBanner id={record.id} onClose={() => setPopup(undefined)} />
                            );
                          }}
                        />
                      </PermissionGuard>
                      {/*<PermissionGuard resource="small-banners" action="delete" fallback={<></>}>*/}
                      {/*  <DeleteButton mutationMode="pessimistic" />*/}
                      {/*</PermissionGuard>*/}
                    </Stack>
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
        <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="md" fullWidth>
          {popup}
        </Dialog>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
